import { TurnStatus, type TurnState } from "../../chat/hooks/useStreamingTurn";
import type { BoltState } from "../brand/Bolt";

/** Compute Bolt's expression for the in-flight assistant turn.
 *
 * The thinking pose is model-agnostic: it is NOT gated on the backend emitting
 * thinking or tool events. As soon as a turn is active and before its first
 * answer token arrives, Bolt looks like it's thinking — so a model that streams
 * a plain answer (no reasoning, no tools) still feels responsive during the
 * wait. Once the answer starts streaming Bolt talks; genuine reasoning (thinking
 * text before the answer, with the thinking feature on) also shows the thinking
 * pose. Idle is the only state that blinks (thinking/talking already animate).
 */
export function computeLiveAvatarState(
  turn: Pick<TurnState, "status" | "thinkingText" | "assistantParts">,
  featureThinking: boolean,
): BoltState {
  const turnActive =
    turn.status === TurnStatus.Connecting ||
    turn.status === TurnStatus.Preparing ||
    turn.status === TurnStatus.Streaming;
  // Genuine reasoning: thinking text is streaming and the answer hasn't started
  // (status flips to Streaming on the first answer chunk).
  const reasoning =
    featureThinking && !!turn.thinkingText && turn.status !== TurnStatus.Streaming;
  const answerStarted = turn.assistantParts.some(
    (p) => p.type === "text" && p.text.length > 0,
  );

  if (reasoning) return "thinking";
  // Always-on: waiting for the first answer token → thinking, regardless of
  // whether the model emits any thinking/tool events.
  if (turnActive && !answerStarted) return "thinking";
  if (turn.status === TurnStatus.Streaming) return "talking";
  return "idle";
}
